import React, { useCallback, useContext, useEffect } from 'react';

import { OAuthContext } from 'common/containers/AuthContainer';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { cn } from 'common/style/utils';

import LazyLoadedImage from './LazyLoadedImage';
import OAuthFlows, { type OAuthProvider } from './OAuthFlows';
import ButtonV2 from './ui/ButtonV2';
import Flex from './ui/Flex';

import AzureLogoIcon from 'img/azure-logo-icon.png';
import FacebookLogoIcon from 'img/facebook-logo-icon.png';
import GitHubLogoIcon from 'img/github-logo-icon.png';
import GoogleLogoIcon from 'img/google-logo-icon.svg';
import OktaLogoIcon from 'img/okta-logo-icon.png';
import OIDCLogoIcon from 'img/open-id-connect-logo.png';

type AuthButtonV2Props = React.ComponentProps<typeof ButtonV2> & {
  authType: OAuthProvider;
  onSuccess: (oauthData: any) => void;
  onFailure: (error: any) => void;
  additionalData?: Record<string, any>;
  queryAllowlist?: Record<string, boolean>;
};

const AuthData: Record<OAuthProvider, { image: string; results: string[] }> = {
  azure: {
    image: AzureLogoIcon,
    results: ['azureAccessToken', 'azureUserID'],
  },
  facebook: {
    image: FacebookLogoIcon,
    results: ['fbUserID', 'fbAccessToken'],
  },
  github: {
    image: GitHubLogoIcon,
    results: ['githubUserID', 'ppToken'],
  },
  google: {
    image: GoogleLogoIcon,
    results: ['googleUserID', 'googleIDToken'],
  },
  gsuite: {
    image: GoogleLogoIcon,
    results: [],
  },
  oidc: {
    image: OIDCLogoIcon,
    results: [],
  },
  okta: {
    image: OktaLogoIcon,
    results: [],
  },
};

const AuthButtonV2 = ({
  authType,
  onSuccess,
  onFailure,
  additionalData = {},
  queryAllowlist = {},
  variant = 'outlined',
  size = 'medium',
  children,
  ...props
}: AuthButtonV2Props) => {
  const oauth = useContext(OAuthContext);
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);

  const authConfig = AuthData[authType];

  const validateOAuthData = useCallback(
    (oauthData: any): string | null => {
      if (!oauthData) {
        return 'OAuth: oauthData is null';
      }

      for (const result of authConfig.results) {
        if (!oauthData[result]) {
          return `OAuth: missing ${result} for ${authType}`;
        }
      }

      return null;
    },
    [authConfig.results, authType],
  );

  const handleOAuthSuccess = useCallback(
    (oauthData: any) => {
      const validationError = validateOAuthData(oauthData);

      if (validationError) {
        onFailure(new Error(validationError));
        return;
      }

      onSuccess(oauthData);
    },
    [validateOAuthData, onFailure, onSuccess],
  );

  const onContinue = useCallback(
    (result: any) => {
      const OAuthFlow = OAuthFlows.getFlow(authType, 2) as any;
      const oauthFlow = new OAuthFlow({
        onContinue,
        onFailure,
        onSuccess,
        redirectURL: null,
        separateWindow: null,
        shouldOpenNewTab: false,
      });
      oauthFlow.connect(result);
    },
    [authType, onFailure, onSuccess],
  );

  const cleanupQuery = useCallback(
    (query: Record<string, any>) => {
      const cleanQuery: Record<string, any> = {};
      for (const [key, value] of Object.entries(query)) {
        if (queryAllowlist[key]) {
          cleanQuery[key] = value;
        }
      }
      return cleanQuery;
    },
    [queryAllowlist],
  );

  useEffect(() => {
    if (!location?.query || location.query.authType !== authType) {
      return;
    }

    const { query } = location;

    if (query.failed) {
      onFailure(query.error);
    } else if (query.success) {
      onSuccess(query);
    } else {
      onContinue(query);
    }

    // Clean query parameters
    const cleanQuery = cleanupQuery(query);
    router.replace({
      pathname: location.pathname,
      query: cleanQuery,
    });
  }, [authType, location, router, onSuccess, onFailure, onContinue, cleanupQuery]);

  const handleClick = useCallback(() => {
    oauth(authType, additionalData, (error: any, oauthData: any) => {
      if (error) {
        onFailure(error);
      } else {
        handleOAuthSuccess(oauthData);
      }
    });
  }, [oauth, authType, additionalData, onFailure, handleOAuthSuccess]);

  return (
    <ButtonV2 variant={variant} size={size} onClick={handleClick} {...props}>
      <Flex className="items-center gap-2 text-[length:inherit]">
        <Flex className="justify-center">
          <LazyLoadedImage
            alt={`${authType} logo`}
            src={authConfig.image}
            className={cn('size-3', { 'size-4': size === 'large' })}
          />
        </Flex>
        {children}
      </Flex>
    </ButtonV2>
  );
};

export default AuthButtonV2;
